/**
 * Telehealth Informed Consent — copy, validation messages and consent body
 * content. Mirrors `AgreementSigningModal.constants.js` so both digital
 * sign flows look and feel identical.
 *
 * NOTE: If you update the body text in TELEHEALTH_CONSENT_CONTENT below,
 * bump `TELEHEALTH_CONSENT_VERSION` so existing clients are prompted to
 * re-sign and a new audit row is recorded in `TelehealthConsents`.
 */

export const TELEHEALTH_MODAL_TITLE =
  "Telehealth Informed Consent (Zoom for Healthcare)";

export const TELEHEALTH_CONSENT_VERSION = "1.0";

export const TELEHEALTH_SIGN_BUTTON_TEXT = "Sign Consent";
export const TELEHEALTH_CANCEL_BUTTON_TEXT = "Cancel";
export const TELEHEALTH_SIGNING_TEXT = "Signing...";

export const TELEHEALTH_SIGNATURE_LABEL =
  "Type your full legal name to sign";
export const TELEHEALTH_SIGNATURE_PLACEHOLDER = "e.g., Jane Marie Smith";
export const TELEHEALTH_CHECKBOX_LABEL =
  "I have read, understand, and voluntarily agree to the Nirva Telehealth Informed Consent above. I acknowledge the benefits, risks, and limitations of receiving services via telehealth.";

export const TELEHEALTH_SUCCESS_MESSAGE =
  "Telehealth consent signed successfully!";
export const TELEHEALTH_ERROR_MESSAGE =
  "Failed to sign telehealth consent. Please try again.";

// Optional client-attested fields shown on the consent form.
export const TELEHEALTH_LOCATION_LABEL =
  "State / region you will be located in during sessions";
export const TELEHEALTH_LOCATION_PLACEHOLDER = "e.g., California";
export const TELEHEALTH_EMERGENCY_NAME_LABEL =
  "Emergency contact name (optional but strongly recommended)";
export const TELEHEALTH_EMERGENCY_NAME_PLACEHOLDER = "e.g., Pat Smith";
export const TELEHEALTH_EMERGENCY_PHONE_LABEL =
  "Emergency contact phone (optional but strongly recommended)";
export const TELEHEALTH_EMERGENCY_PHONE_PLACEHOLDER =
  "e.g., (555) 123-4567";

// Parent / Legal Guardian fields (for minor clients ages 14-17)
export const TELEHEALTH_GUARDIAN_SECTION_TITLE =
  "Parent / Legal Guardian Acknowledgment";
export const TELEHEALTH_GUARDIAN_SECTION_NOTICE =
  "Because the client is between 14-17 years of age, a parent or legal guardian must also acknowledge and sign this telehealth informed consent before proceeding.";
export const TELEHEALTH_GUARDIAN_NAME_LABEL =
  "Parent / Legal Guardian Full Name";
export const TELEHEALTH_GUARDIAN_NAME_PLACEHOLDER = "e.g., John David Smith";
export const TELEHEALTH_GUARDIAN_SIGNATURE_LABEL =
  "Parent / Legal Guardian Signature (type full legal name)";
export const TELEHEALTH_GUARDIAN_SIGNATURE_PLACEHOLDER =
  "Type full legal name to sign";
export const TELEHEALTH_GUARDIAN_RELATIONSHIP_LABEL = "Relationship to Minor";
export const TELEHEALTH_GUARDIAN_EMAIL_LABEL =
  "Parent / Legal Guardian Email";
export const TELEHEALTH_GUARDIAN_EMAIL_PLACEHOLDER = "e.g., parent@email.com";
export const TELEHEALTH_GUARDIAN_RELATIONSHIP_OPTIONS = [
  "Parent",
  "Legal Guardian",
  "Other",
];

/**
 * Body of the Telehealth Informed Consent shown to the client in the modal.
 * The structure (sections[] with heading / subtitle / content / items) is
 * intentionally identical to AGREEMENT_CONTENT so the same rendering loop
 * can be reused.
 *
 * Content derived from the Nirva Telehealth Informed Consent (Zoom for
 * Healthcare) PDF and the Nirva Terms of Service §6 (Telehealth &
 * Technology). Update wording here in collaboration with clinical/legal
 * review and bump TELEHEALTH_CONSENT_VERSION when changes are material.
 */
export const TELEHEALTH_CONSENT_CONTENT = {
  title: "Nirva Telehealth Informed Consent (Zoom for Healthcare)",
  effectiveDate: "June 2026",
  sections: [
    {
      heading: "Introduction",
      content:
        "Thank you for choosing Nirva for your interpersonal hypnotherapy services. Nirva delivers sessions via telehealth using Zoom for Healthcare or an equivalent HIPAA-eligible video platform. This Telehealth Informed Consent describes the nature, potential benefits, risks, and limitations of receiving services remotely so you can make an informed decision about your participation.",
    },
    {
      heading: "1. Nature of Telehealth Services",
      content:
        "Telehealth involves the delivery of hypnotherapy and related wellness services through interactive audio and video technology, allowing your practitioner and you to interact in real time without being in the same physical location. Sessions may include intake interviews, hypnotherapy, guided visualization, coaching conversations, and educational discussion. Hypnotherapy is a complementary wellness modality and is not a substitute for medical, psychological, or psychiatric care.",
    },
    {
      heading: "2. Potential Benefits",
      items: [
        "Convenient access to qualified practitioners regardless of geographic distance.",
        "Greater scheduling flexibility and reduced travel time.",
        "Ability to participate from a private, familiar environment of your choosing.",
        "Continuity of care during travel, relocation, or limited mobility.",
      ],
    },
    {
      heading: "3. Potential Risks & Limitations",
      items: [
        "Technology disruptions (e.g., dropped calls, frozen video, audio lag) may interrupt or delay sessions.",
        "Telehealth may feel less personal than in-person sessions, and some non-verbal cues may be harder to detect.",
        "Despite reasonable safeguards, electronic communications can never be guaranteed 100% secure; there is some inherent risk that transmissions could be intercepted or accessed by unauthorized parties.",
        "Telehealth is not appropriate for crisis situations, acute psychiatric emergencies, or conditions that require in-person evaluation.",
        "Hypnotherapy outcomes vary by individual and are not guaranteed.",
      ],
    },
    {
      heading: "4. Confidentiality & Privacy",
      content:
        "Nirva uses Zoom for Healthcare or an equivalent HIPAA-eligible platform that supports encryption in transit and a Business Associate Agreement. Your practitioner will not record sessions without your prior written authorization. You agree not to record, screenshot, or save chat messages from your session without the practitioner's prior written consent. Standard limits to confidentiality apply: practitioners may be required by law to disclose information when there is risk of harm to yourself or others, suspected abuse or neglect of a child, elder, or dependent adult, or when ordered by a court of competent jurisdiction.",
    },
    {
      heading: "5. Your Environment & Technical Requirements",
      items: [
        "Use a private, quiet, and comfortable space where you will not be overheard or interrupted.",
        "Use headphones whenever possible to protect your privacy and improve audio quality.",
        "Use a stable broadband internet connection and a device with a working camera, microphone, and speaker.",
        "Disclose to your practitioner anyone present in the room or within hearing range during the session.",
        "Do not participate in sessions while operating a vehicle or heavy machinery.",
      ],
    },
    {
      heading: "6. Location & Jurisdiction",
      content:
        "Because telehealth services are regulated by the jurisdiction in which the client is physically located at the time of the session, you agree to confirm your physical location (state/region) with your practitioner at the start of each session and to notify Nirva promptly if your primary location changes. You attest that the state/region you provide on this form is the state/region in which you will be physically present during sessions.",
    },
    {
      heading: "7. Emergencies",
      content:
        "Nirva and its practitioners do not provide emergency services and are not available 24/7. If you are experiencing a medical or mental-health emergency, call 911 (or your local emergency number) or go to the nearest emergency department immediately. In the United States you may also call or text 988 to reach the Suicide & Crisis Lifeline. Providing an emergency contact below is optional but strongly recommended so your practitioner can support your safety planning during virtual sessions.",
    },
    {
      heading: "8. Recordings",
      content:
        "Practitioner-created hypnotic programming recordings may be offered for your personal use and are shared via secure methods. Any other recording of a session by you or your practitioner requires specific written authorization and Nirva approval.",
    },
    {
      heading: "9. Fees, Cancellations, & Payment",
      content:
        "Fees for telehealth sessions are the same as those described in your Client Participation Agreement and in any practitioner-specific rate disclosures. Cancellation and no-show policies apply equally to telehealth sessions. A valid payment method must be on file before booking paid sessions.",
    },
    {
      heading: "10. Right to Withdraw Consent",
      content:
        "Your participation in telehealth is voluntary. You may withdraw your consent to receive services via telehealth at any time without penalty by notifying your practitioner or Nirva in writing. Withdrawal of consent does not affect any services previously provided under this consent.",
    },
    {
      heading: "11. Acknowledgment",
      content:
        "By signing below, I acknowledge that I have read this Telehealth Informed Consent, that I have had an opportunity to ask questions and have them answered to my satisfaction, that I understand the potential benefits and risks of telehealth, and that I voluntarily consent to receiving services from Nirva practitioners via telehealth using Zoom for Healthcare or an equivalent HIPAA-eligible platform under the terms described above.",
    },
  ],
};
